/**
 * CustomTimeInput - Custom time input component to replace native time input
 * This allows full control over styling, including removing borders from the popup
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface CustomTimeInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  title?: string;
  style?: React.CSSProperties;
  className?: string;
  inputRef?: (el: HTMLInputElement | null) => void;
}

export const CustomTimeInput: React.FC<CustomTimeInputProps> = ({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  disabled = false,
  readOnly = false,
  title,
  style,
  className = '',
  inputRef
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [period, setPeriod] = useState<'AM' | 'PM'>('AM');
  const [pickerPosition, setPickerPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRefInternal = useRef<HTMLInputElement>(null);

  // Parse value on mount and when value changes
  useEffect(() => {
    if (value) {
      const [timePart] = value.split(' ');
      if (timePart) {
        const [h, m] = timePart.split(':');
        if (h && m) {
          const hourNum = parseInt(h, 10);
          if (hourNum >= 12) {
            setPeriod('PM');
            setHours(hourNum === 12 ? '12' : String(hourNum - 12).padStart(2, '0'));
          } else {
            setPeriod('AM');
            setHours(hourNum === 0 ? '12' : String(hourNum).padStart(2, '0'));
          }
          setMinutes(m);
        }
      }
    } else {
      setHours('00');
      setMinutes('00');
      setPeriod('AM');
    }
  }, [value]);

  // Calculate picker position when opened and update on scroll
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const pickerHeight = 200; // approximate height
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          
          // If not enough space below but enough space above, flip upward
          const shouldFlipUp = spaceBelow < pickerHeight && spaceAbove > pickerHeight;
          
          setPickerPosition({
            top: shouldFlipUp ? rect.top - pickerHeight - 2 : rect.bottom + 2,
            left: rect.left
          });
        }
      };

      // Initial position
      updatePosition();

      // Update position on scroll (capture phase to catch all scroll events)
      const scrollableParents = containerRef.current ? 
        Array.from(document.querySelectorAll('.universal-table-container')) : [];
      
      scrollableParents.forEach(parent => {
        parent.addEventListener('scroll', updatePosition, true);
      });
      
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        scrollableParents.forEach(parent => {
          parent.removeEventListener('scroll', updatePosition, true);
        });
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    } else {
      setPickerPosition(null);
    }
  }, [isOpen]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest('.custom-time-picker-popup'))
      ) {
        setIsOpen(false);
        if (onBlur) onBlur();
      }
    };

    if (isOpen) {
      // Use a small delay to avoid closing immediately when opening
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onBlur]);

  // Format time value
  const formatTime = (h: string, m: string, p: 'AM' | 'PM'): string => {
    let hourNum = parseInt(h, 10);
    if (p === 'PM' && hourNum !== 12) {
      hourNum += 12;
    } else if (p === 'AM' && hourNum === 12) {
      hourNum = 0;
    }
    return `${String(hourNum).padStart(2, '0')}:${m}`;
  };

  const handleSelect = (newHours?: string, newMinutes?: string, newPeriod?: 'AM' | 'PM') => {
    const finalHours = newHours !== undefined ? newHours : hours;
    const finalMinutes = newMinutes !== undefined ? newMinutes : minutes;
    const finalPeriod = newPeriod !== undefined ? newPeriod : period;
    const timeValue = formatTime(finalHours, finalMinutes, finalPeriod);
    onChange(timeValue);
    setIsOpen(false);
    if (onBlur) onBlur();
  };

  const handleFocus = () => {
    if (!disabled && !readOnly) {
      setIsOpen(true);
      if (onFocus) onFocus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || readOnly) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      if (onBlur) onBlur();
    } else if (e.key === 'Tab') {
      setIsOpen(false);
    } else {
      if (onKeyDown) onKeyDown(e as any);
    }
  };

  // Combine refs
  useEffect(() => {
    if (inputRef && inputRefInternal.current) {
      inputRef(inputRefInternal.current as any);
    }
  }, [inputRef]);

  // Force normal font weight using setProperty to override any CSS rules
  useEffect(() => {
    if (inputRefInternal.current) {
      inputRefInternal.current.style.setProperty('font-weight', '400', 'important');
    }
  }, [value, disabled, readOnly]);

  const displayValue = value || '--:--';
  const hourOptions = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  return (
    <div 
      ref={containerRef}
      className={`custom-time-input-container ${className}`}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <div
        ref={inputRefInternal as any}
        className="custom-time-input-display"
        style={{
          ...style,
          width: '100%',
          height: '100%',
          padding: style?.padding || '4px 8px',
          border: 'none',
          borderWidth: 0,
          borderStyle: 'none',
          borderColor: 'transparent',
          borderLeft: 'none',
          borderRight: 'none',
          borderTop: 'none',
          borderBottom: 'none',
          outline: 'none',
          boxShadow: 'none',
          background: style?.background || (value ? '#fff' : '#f9fafb'),
          color: style?.color || '#000000',
          cursor: disabled || readOnly ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: style?.fontSize || '12px',
          fontWeight: style?.fontWeight || 400,
          textAlign: style?.textAlign || 'center',
          fontFamily: style?.fontFamily || 'inherit',
          boxSizing: 'border-box',
          opacity: disabled ? 0.6 : 1,
          userSelect: 'none' as const,
          margin: 0
        }}
        onClick={handleFocus}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        tabIndex={disabled || readOnly ? -1 : 0}
        title={title}
        role="textbox"
        aria-disabled={disabled || readOnly}
      >
        {displayValue}
      </div>

      {isOpen && !disabled && !readOnly && pickerPosition && createPortal(
        <div
          className="custom-time-picker-popup"
          style={{
            position: 'fixed',
            top: `${pickerPosition.top}px`,
            left: `${pickerPosition.left}px`,
            zIndex: 99999,
            backgroundColor: '#ffffff',
            border: 'none',
            borderWidth: 0,
            borderStyle: 'none',
            borderColor: 'transparent',
            outline: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            borderRadius: '4px',
            marginTop: '2px',
            padding: '8px',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Hours */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Hour</div>
            <div style={{ 
              maxHeight: '150px', 
              overflowY: 'auto',
              border: 'none',
              borderWidth: 0,
              borderStyle: 'none',
              borderColor: 'transparent',
              outline: 'none'
            }}>
              {hourOptions.map((h) => (
                <div
                  key={h}
                  onClick={() => {
                    setHours(h);
                    handleSelect(h, undefined, undefined);
                  }}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    backgroundColor: hours === h ? '#e6f4fb' : '#ffffff',
                    color: '#000000',
                    border: 'none',
                    borderWidth: 0,
                    borderStyle: 'none',
                    borderColor: 'transparent',
                    outline: 'none',
                    fontSize: '12px',
                    fontWeight: hours === h ? 600 : 400,
                    textAlign: 'center',
                    minWidth: '40px'
                  }}
                >
                  {h}
                </div>
              ))}
            </div>
          </div>

          {/* Minutes */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Min</div>
            <div style={{ 
              maxHeight: '150px', 
              overflowY: 'auto',
              border: 'none',
              borderWidth: 0,
              borderStyle: 'none',
              borderColor: 'transparent',
              outline: 'none'
            }}>
              {minuteOptions.map((m) => (
                <div
                  key={m}
                  onClick={() => {
                    setMinutes(m);
                    handleSelect(undefined, m, undefined);
                  }}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    backgroundColor: minutes === m ? '#e6f4fb' : '#ffffff',
                    color: '#000000',
                    border: 'none',
                    borderWidth: 0,
                    borderStyle: 'none',
                    borderColor: 'transparent',
                    outline: 'none',
                    fontSize: '12px',
                    fontWeight: minutes === m ? 600 : 400,
                    textAlign: 'center',
                    minWidth: '40px'
                  }}
                >
                  {m}
                </div>
              ))}
            </div>
          </div>

          {/* AM/PM */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '10px', color: '#666', marginBottom: '4px' }}>Period</div>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              border: 'none',
              borderWidth: 0,
              borderStyle: 'none',
              borderColor: 'transparent',
              outline: 'none'
            }}>
              {(['AM', 'PM'] as const).map((p) => (
                <div
                  key={p}
                  onClick={() => {
                    setPeriod(p);
                    handleSelect(undefined, undefined, p);
                  }}
                  style={{
                    padding: '6px 12px',
                    cursor: 'pointer',
                    backgroundColor: period === p ? '#e6f4fb' : '#ffffff',
                    color: '#000000',
                    border: 'none',
                    borderWidth: 0,
                    borderStyle: 'none',
                    borderColor: 'transparent',
                    outline: 'none',
                    fontSize: '12px',
                    fontWeight: period === p ? 600 : 400,
                    textAlign: 'center',
                    minWidth: '40px'
                  }}
                >
                  {p}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

